import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import styled from 'styled-components'

import { ErrorBlock, ModalLoading } from '@client/components/molecules'
import { UserAddressFragment } from '@hooks/api'

import { EditAddressForm } from '../forms'
import { AddAddressForm } from '../forms/AddAddressForm'

enum AddressFormStepEnum {
  ADD_ADDRESS = 'ADD_ADDRESS',
  EDIT_ADDRESS = 'EDIT_ADDRESS',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`

export interface AddressFormProps {
  userAddress?: UserAddressFragment

  onSuccess?: () => void
  onCancel?: () => void

  actionButtonText?: string

  secondaryActionButtonText?: string

  displayActionButtons?: boolean
  className?: string

  disabled?: boolean
  checkoutFlowType?: string
}

interface AddressFormState {
  displayStep: AddressFormStepEnum
  error: string
}

const DEFAULT_STATE: AddressFormState = {
  displayStep: AddressFormStepEnum.ADD_ADDRESS,
  error: '',
}

export function AddressForm({ userAddress, onSuccess, onCancel, actionButtonText, secondaryActionButtonText, displayActionButtons, className, disabled, checkoutFlowType }: AddressFormProps): JSX.Element {
  const [state, setState] = useState<AddressFormState>({ ...DEFAULT_STATE })

  const _handleCancel = (): void => {
    if (onCancel) {
      onCancel()
    }
  }

  const _handleSuccess = (): void => {
    if (onSuccess) {
      onSuccess()
    }
  }

  const _handleError = (message: string): void => {
    setState((prevState) => update(prevState, {
      displayStep: { $set: AddressFormStepEnum.ERROR },
      error: { $set: message },
    }))
  }

  useEffect(() => {
    if (userAddress) {
      setState((prevState) => update(prevState, {
        displayStep: { $set: AddressFormStepEnum.EDIT_ADDRESS },
      }))
    }
  }, [userAddress])

  const conditionalCancelProp = onCancel ? { onCancel: _handleCancel } : {}

  return (
    <Container className={className}>
      <Choose>
        <When condition={state.displayStep === AddressFormStepEnum.LOADING}>
          <ModalLoading message={`Attempting to ${userAddress ? 'update existing' : 'add a new'} address`} />
        </When>
        <When condition={state.displayStep === AddressFormStepEnum.ERROR}>
          <ErrorBlock
            title={state.error}
            actionTitle='Back'
            onClick={() => setState((prevState) => ({ ...prevState, displayStep: AddressFormStepEnum.ADD_ADDRESS }))} />
        </When>
        <When condition={state.displayStep === AddressFormStepEnum.EDIT_ADDRESS && !!userAddress}>
          <EditAddressForm
            userAddress={userAddress}
            onSuccess={_handleSuccess}
            onClose={_handleCancel}
            onError={(message) => _handleError(message)} />
        </When>
        <Otherwise>
          <AddAddressForm
            actionButtonText={actionButtonText}
            secondaryActionButtonText={secondaryActionButtonText}
            displayActionButtons={displayActionButtons}
            onSuccess={_handleSuccess}
            onError={(message) => _handleError(message)}
            disabled={typeof disabled === 'boolean' ? disabled : false}
            checkoutFlowType={checkoutFlowType}
            {...conditionalCancelProp}
          />
        </Otherwise>
      </Choose>
    </Container>
  )
}
